import { useMemo } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { formatDate } from '@/utils/formatters';
import { useInvestorDrawdowns } from '../hooks/useInvestorDrawdowns';
import { useInvestorDistributions } from '../hooks/useInvestorDistributions';
import { usePaymentsLastUpdated } from '../hooks/usePaymentsLastUpdated';
import { DrawdownListItem } from '../components/DrawdownListItem';
import { TransactionActivity } from '../components/TransactionActivity';
import type { InvestorDrawdownSummary } from '../services/drawdowns-service';

function TransactionsLoadingSkeleton() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Transactions</h1>
        <p className="text-muted-foreground mt-1">
          Capital calls, distributions, and payment history
        </p>
      </div>
      <div className="space-y-4 animate-pulse">
        <div className="flex gap-2">
          <div className="h-10 w-20 bg-muted rounded" />
          <div className="h-10 w-28 bg-muted rounded" />
          <div className="h-10 w-28 bg-muted rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-5 w-32 bg-muted rounded" />
          <div className="h-14 bg-muted rounded-lg" />
          <div className="h-14 bg-muted rounded-lg" />
          <div className="h-14 bg-muted rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function ComingSoonState() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Transactions</h1>
        <p className="text-muted-foreground mt-1">
          Capital calls, distributions, and payment history
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Clock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Coming Soon
          </h3>
          <p className="text-muted-foreground text-center max-w-md">
            Your fund manager hasn&apos;t published transactions yet.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function PaymentsDueSection({
  pendingCalls,
  hasDrawdowns,
}: {
  pendingCalls: InvestorDrawdownSummary[];
  hasDrawdowns: boolean;
}) {
  if (pendingCalls.length > 0) {
    return (
      <section>
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Payments Due
          </h2>
          <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-xs font-bold">
            {pendingCalls.length}
          </span>
        </div>
        <div className="space-y-3">
          {pendingCalls.map((drawdown) => (
            <DrawdownListItem
              key={drawdown.id}
              drawdown={drawdown}
              variant="urgent"
            />
          ))}
        </div>
      </section>
    );
  }

  if (hasDrawdowns) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        <span>All payments up to date</span>
      </div>
    );
  }

  return null;
}

export function PortalTransactionsPage() {
  const { data: drawdowns = [], isLoading: isLoadingDrawdowns } =
    useInvestorDrawdowns();
  const { data: distributionsList, isLoading: isLoadingDistributions } =
    useInvestorDistributions();
  const { data: paymentsLastUpdated } = usePaymentsLastUpdated();

  const { pendingCalls, paidCalls } = useMemo(() => {
    const overdue = drawdowns.filter(
      (d) => d.investor_allocation.status === 'overdue'
    );
    const pending = drawdowns.filter(
      (d) =>
        d.investor_allocation.status === 'pending' ||
        d.investor_allocation.status === 'partial'
    );
    return {
      pendingCalls: [...overdue, ...pending],
      paidCalls: drawdowns.filter(
        (d) => d.investor_allocation.status === 'paid'
      ),
    };
  }, [drawdowns]);

  if (isLoadingDrawdowns || isLoadingDistributions) {
    return <TransactionsLoadingSkeleton />;
  }

  const distributions = distributionsList?.distributions ?? [];
  const hasNoTransactions =
    drawdowns.length === 0 && distributions.length === 0;

  if (hasNoTransactions && (distributionsList?.hidden_fund_count ?? 0) > 0) {
    return <ComingSoonState />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Transactions</h1>
        <p className="text-muted-foreground mt-1">
          Capital calls, distributions, and payment history
        </p>
        {paymentsLastUpdated && (
          <p className="text-sm text-muted-foreground mt-1">
            Last update: {formatDate(paymentsLastUpdated)}
          </p>
        )}
      </div>

      {hasNoTransactions ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg">No transactions yet</p>
          <p className="text-sm mt-1">
            Capital calls and distributions will appear here when issued
          </p>
        </div>
      ) : (
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="capital-calls">Capital Calls</TabsTrigger>
            <TabsTrigger value="distributions">Distributions</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6 space-y-8">
            <PaymentsDueSection
              pendingCalls={pendingCalls}
              hasDrawdowns={drawdowns.length > 0}
            />
            <TransactionActivity
              paidDrawdowns={paidCalls}
              distributions={distributions}
            />
          </TabsContent>

          <TabsContent value="capital-calls" className="mt-6 space-y-8">
            <PaymentsDueSection
              pendingCalls={pendingCalls}
              hasDrawdowns={drawdowns.length > 0}
            />
            <TransactionActivity
              title="Paid"
              paidDrawdowns={paidCalls}
              distributions={[]}
            />
          </TabsContent>

          <TabsContent value="distributions" className="mt-6 space-y-8">
            <TransactionActivity
              paidDrawdowns={[]}
              distributions={distributions}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
